import { clamp, genId } from '~/lib/utils';
import type { BgColor, KeyedRegion } from '~/types/pipeline';
import { BG_ALPHA_MIN } from './detect';
import { minBgDistance, outerThreshold } from './remove';

export const REGION_MAX_PIXELS = 50_000;
const SEED_FALLBACK_TOLERANCE = 32;

/** Color-key gate matching `removeBackground`'s outer threshold. */
export function colorKeyGate(
  image: ImageData,
  bgColors: readonly BgColor[],
  tolerance: number,
  hardEdge: boolean,
): (x: number, y: number) => boolean {
  const outerT = outerThreshold(tolerance, hardEdge);
  const { width, data } = image;
  return (x, y) => minBgDistance(data, (y * width + x) * 4, bgColors).dist <= outerT;
}

/** BFS 4-connected from the seed pixel through cells where `gate(x, y)` is
 *  true and alpha ≥ BG_ALPHA_MIN. Returns null if the seed is OOB, transparent,
 *  or doesn't pass the gate. Caps visited count at `maxPixels`.
 *  `maxRadius` — Chebyshev distance from seed; neighbors beyond it are skipped. */
export function floodKeyRegion(
  image: ImageData,
  seedX: number,
  seedY: number,
  gate: (x: number, y: number) => boolean,
  maxPixels: number,
  generateId: () => string = genId,
  maxRadius?: number | null,
): KeyedRegion | null {
  const { width: w, height: h, data } = image;
  if (seedX < 0 || seedX >= w || seedY < 0 || seedY >= h) return null;
  const seedI = seedY * w + seedX;
  if ((data[seedI * 4 + 3] ?? 0) < BG_ALPHA_MIN) return null;
  if (!gate(seedX, seedY)) return null;

  // Scope the visited/inRegion buffers to the radius-bounded window when
  // `maxRadius` is provided — otherwise large sources allocate w*h * 2 bytes
  // per click. Window is clipped to image bounds; coords are remapped into it.
  const wx0 = maxRadius != null ? Math.max(0, seedX - maxRadius) : 0;
  const wy0 = maxRadius != null ? Math.max(0, seedY - maxRadius) : 0;
  const wx1 = maxRadius != null ? Math.min(w - 1, seedX + maxRadius) : w - 1;
  const wy1 = maxRadius != null ? Math.min(h - 1, seedY + maxRadius) : h - 1;
  const ww = wx1 - wx0 + 1;
  const wh = wy1 - wy0 + 1;
  const localIdx = (x: number, y: number) => (y - wy0) * ww + (x - wx0);

  // `visited` records both rejected and accepted cells (to prevent re-testing).
  // `inRegion` tracks accepted cells only, then is sliced to the bbox.
  const visited = new Uint8Array(ww * wh);
  const inRegion = new Uint8Array(ww * wh);
  const stack: number[] = [seedI];
  visited[localIdx(seedX, seedY)] = 1;
  inRegion[localIdx(seedX, seedY)] = 1;
  let xMin = seedX;
  let xMax = seedX;
  let yMin = seedY;
  let yMax = seedY;
  let pixelCount = 0;

  while (stack.length > 0 && pixelCount < maxPixels) {
    const i = stack.pop() as number;
    pixelCount++;
    const x = i % w;
    const y = (i - x) / w;
    if (x < xMin) xMin = x;
    else if (x > xMax) xMax = x;
    if (y < yMin) yMin = y;
    else if (y > yMax) yMax = y;

    const tryAdd = (nx: number, ny: number) => {
      if (nx < wx0 || nx > wx1 || ny < wy0 || ny > wy1) return;
      if (maxRadius != null && Math.max(Math.abs(nx - seedX), Math.abs(ny - seedY)) > maxRadius)
        return;
      const li = localIdx(nx, ny);
      if (visited[li]) return;
      visited[li] = 1;
      const ni = ny * w + nx;
      if ((data[ni * 4 + 3] ?? 0) < BG_ALPHA_MIN) return;
      if (!gate(nx, ny)) return;
      inRegion[li] = 1;
      stack.push(ni);
    };
    tryAdd(x - 1, y);
    tryAdd(x + 1, y);
    tryAdd(x, y - 1);
    tryAdd(x, y + 1);
  }

  const bw = xMax - xMin + 1;
  const bh = yMax - yMin + 1;
  const mask = new Uint8Array(bw * bh);
  for (let y = yMin; y <= yMax; y++) {
    for (let x = xMin; x <= xMax; x++) {
      if (inRegion[localIdx(x, y)]) mask[(y - yMin) * bw + (x - xMin)] = 255;
    }
  }

  smoothMaskEdges(mask, bw, bh);

  return {
    id: generateId(),
    bbox: { x: xMin, y: yMin, w: bw, h: bh },
    mask,
    pixelCount,
    seed: { x: seedX, y: seedY },
    tolerance: 0,
  };
}

/**
 * Flood from `seed` using `bgColors` + `tolerance` + `hardEdge`. Optionally
 * gates on `cleaned` alpha. Returns null if seed is OOB or fails the gate.
 * Applies edge smoothing. Preserves `id` when provided.
 */
export function floodFromSeed(
  image: ImageData,
  bgColors: readonly BgColor[],
  seed: { x: number; y: number },
  tolerance: number,
  hardEdge: boolean,
  cleaned: ImageData | null,
  id?: string,
): KeyedRegion | null {
  const { width: iw } = image;
  const maxRadius = clamp(32, 512, tolerance * 4);

  const cleanedAlphaGate = cleaned
    ? (x: number, y: number) => (cleaned.data[(y * iw + x) * 4 + 3] ?? 255) > 0
    : null;
  const compose = (base: (x: number, y: number) => boolean) =>
    cleanedAlphaGate ? (x: number, y: number) => cleanedAlphaGate(x, y) && base(x, y) : base;

  const generateId = id ? () => id : genId;

  const baseGlobalGate =
    bgColors.length > 0 ? colorKeyGate(image, bgColors, tolerance, hardEdge) : null;
  const globalGate = baseGlobalGate ? compose(baseGlobalGate) : null;
  const seedPassesGlobalGate = globalGate?.(seed.x, seed.y) ?? false;
  let region: KeyedRegion | null = null;
  if (seedPassesGlobalGate && globalGate) {
    region = floodKeyRegion(
      image,
      seed.x,
      seed.y,
      globalGate,
      REGION_MAX_PIXELS,
      generateId,
      maxRadius,
    );
  }

  if (!region) {
    const { data, width } = image;
    const i = (seed.y * width + seed.x) * 4;
    const baseSeedGate = colorKeyGate(
      image,
      [{ rgb: [data[i] ?? 0, data[i + 1] ?? 0, data[i + 2] ?? 0], weight: 1 }],
      Math.min(tolerance, SEED_FALLBACK_TOLERANCE),
      hardEdge,
    );
    region = floodKeyRegion(
      image,
      seed.x,
      seed.y,
      compose(baseSeedGate),
      REGION_MAX_PIXELS,
      generateId,
      maxRadius,
    );
  }

  if (region) region.tolerance = tolerance;
  return region;
}

/**
 * Morphological closing (dilate then erode, radius 1) on a binary mask.
 * Fills single-pixel gaps/jaggies on the boundary. Mutates mask in-place.
 * Only scans the tight bbox — exterior stays 0.
 */
function morphClose(mask: Uint8Array, w: number, h: number): void {
  const tmp = new Uint8Array(mask.length);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = y * w + x;
      if (
        (mask[i] ?? 0) > 0 ||
        (x > 0 && (mask[i - 1] ?? 0) > 0) ||
        (x < w - 1 && (mask[i + 1] ?? 0) > 0) ||
        (y > 0 && (mask[i - w] ?? 0) > 0) ||
        (y < h - 1 && (mask[i + w] ?? 0) > 0)
      ) {
        tmp[i] = 255;
      }
    }
  }
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = y * w + x;
      if (
        (tmp[i] ?? 0) === 0 ||
        (x > 0 && (tmp[i - 1] ?? 0) === 0) ||
        (x < w - 1 && (tmp[i + 1] ?? 0) === 0) ||
        (y > 0 && (tmp[i - w] ?? 0) === 0) ||
        (y < h - 1 && (tmp[i + w] ?? 0) === 0)
      ) {
        mask[i] = 0;
      } else {
        mask[i] = 255;
      }
    }
  }
}

/**
 * One pass of 3×3 box blur on boundary pixels only. Interior stays 255,
 * exterior stays 0. Mutates mask in-place.
 */
function featherPass(mask: Uint8Array, w: number, h: number): void {
  const blurred = new Uint8Array(mask.length);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = y * w + x;
      if ((mask[i] ?? 0) === 0) continue;
      const isBoundary =
        x === 0 ||
        x === w - 1 ||
        y === 0 ||
        y === h - 1 ||
        (mask[(y - 1) * w + x] ?? 0) === 0 ||
        (mask[(y + 1) * w + x] ?? 0) === 0 ||
        (mask[y * w + (x - 1)] ?? 0) === 0 ||
        (mask[y * w + (x + 1)] ?? 0) === 0;
      if (!isBoundary) {
        blurred[i] = 255;
        continue;
      }
      let sum = 0;
      for (let dy = -1; dy <= 1; dy++) {
        for (let dx = -1; dx <= 1; dx++) {
          const ny = y + dy;
          const nx = x + dx;
          // OOB pixels count as 0 — they pull boundary values down.
          if (ny >= 0 && ny < h && nx >= 0 && nx < w) sum += mask[ny * w + nx] ?? 0;
        }
      }
      blurred[i] = clamp(0, 255, Math.round(sum / 9));
    }
  }
  mask.set(blurred);
}

/**
 * Smooths flood-fill mask edges: morphological closing removes single-pixel
 * jaggies, then two feather passes soften the boundary. Mutates mask in-place.
 */
function smoothMaskEdges(mask: Uint8Array, w: number, h: number): void {
  morphClose(mask, w, h);
  featherPass(mask, w, h);
  featherPass(mask, w, h);
}

/** Newest-first hit test. Returns the first region whose bbox contains (x, y)
 *  AND whose mask is non-zero there. */
export function findRegionAt(
  regions: readonly KeyedRegion[],
  x: number,
  y: number,
): KeyedRegion | null {
  for (let i = regions.length - 1; i >= 0; i--) {
    const r = regions[i];
    if (!r) continue;
    const { bbox } = r;
    if (x < bbox.x || x >= bbox.x + bbox.w || y < bbox.y || y >= bbox.y + bbox.h) continue;
    const lx = x - bbox.x;
    const ly = y - bbox.y;
    if ((r.mask[ly * bbox.w + lx] ?? 0) > 0) return r;
  }
  return null;
}
